import { RouterModule } from '@angular/router';
import { NgxsModule } from '@ngxs/store';
import { ReactiveFormsModule } from '@angular/forms';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import {
  ButtonsModule,
  DropDownButtonModule
} from '@progress/kendo-angular-buttons';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { GridModule } from '@progress/kendo-angular-grid';
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { PhoneNumberPipe } from './pipes/phone-number/phone-number.pipe';
import { RolesDropDownComponent } from './components/roles-drop-down/roles-drop-down.component';
import { DropdownFilterComponent } from './components/dropdown-filter/dropdown-filter.component';
import { DateFormatPipe } from './pipes/date/date.pipe';
import { InvoiceCyclePipe } from './pipes/invoice-cycle/invoice-cycle.pipe';
import { LobPipe } from './pipes/lob/lob.pipe';
import { StatusPipe } from './pipes/status/status.pipe';
import { StatusCellComponent } from './components/status-cell/status-cell.component';
import { IconsModule } from '@progress/kendo-angular-icons';
import { CbxSelectUnselectComponent } from './components/cbx-select-unselect/cbx-select-unselect.component';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { BooleanStatusCellComponent } from './components/boolean-status-cell/boolean-status-cell.component';
import { UserPipe } from './pipes/user/user.pipe';
import { StateRolesDropdownService } from './states/state-roles-dropdown/state-roles-dropdown.service';
import { RoleModule } from '@module-role/module-role.module';
import { CellGlobalStatusComponent } from './components/cell-global-status/cell-global-status.component';
import { AmendmentActionPipe } from './pipes/amendment-action/amendment-action.pipe';
import { BenefitDesignApprovalStatusPipe } from './pipes/benefit-design-approval-status/benefit-design-approval-status.pipe';
import { FileItemComponent } from './components/file-item/file-item.component';
import { FileSizePipe } from './pipes/file-size/file-size.pipe';
import { FileNamePipe } from './pipes/file-name/file-name.pipe';
import { PreloaderComponent } from './components/preloader/preloader.component';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { LinkOrTextComponent } from './components/link-or-text/link-or-text.component';
import { BenefitDesignLobPipe } from './pipes/benefit-design-lob/benefit-design-lob.pipe';
import { InvoiceStatusPipe } from './pipes/invoice-status/invoice-status.pipe';
import { ContractLobPipe } from './pipes/contract-lob/contract-lob.pipe';
import { ContractStatusPipe } from './pipes/contract-status/contract-status.pipe';
import { MultiselectFilterComponent } from './components/multiselect-filter/multiselect-filter.component';
import { HealthPlanStatusPipe } from './pipes/health-plan-status/health-plan-status.pipe';
import { BlockchainStatusComponent } from './components/blockchain-status/blockchain-status.component';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { StateBlockchainService } from './states/state-blockchain/state-blockchain.service';
import { BlockchainHttpService } from './services/blockchain-http/blockchain-http.service';
import { GenericAmendmentsTableComponent } from './components/generic-amendments-table/generic-amendments-table.component';
import { ClaimCategoryPipe } from './pipes/claim-category/claim-category.pipe';
import { ClaimFileStatusLatestPipe } from './pipes/claim-file-status-latest/claim-file-status-latest.pipe';
import { ClaimFileStatusLogPipe } from './pipes/claim-file-status-log/claim-file-status-log.pipe';
import { GridCellClaimFileStatusComponent } from './components/grid-cell-claim-file-status/grid-cell-claim-file-status.component';
import { BaseLandingComponent } from './components/base-landing/base-landing.component';
import { DatepickerClearDirective } from './directives/datepicker-clear/datepicker-clear.directive';
import { ShortNumberPipe } from './pipes/short-number/short-number.pipe';
import { TooltipOnEllipsisDirective } from './directives/tooltip-on-ellipsis/tooltip-on-ellipsis.directive';
import { BooleanYesNoPipe } from './pipes/boolean-yes-no/boolean-yes-no.pipe';
import { ComboboxValueToTextPipe } from './pipes/combobox-value-to-text/combobox-value-to-text.pipe';
import { ArraySeparatorPipe } from './pipes/array-separator/array-separator.pipe';
import { SplitGridCellComponent } from './components/split-grid-cell/split-grid-cell.component';
import { PieChartCellComponent } from './components/pie-chart-cell/pie-chart-cell.component';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { ConfigurableGridComponent } from './components/configurable-grid/configurable-grid.component';
import { GuaranteedRebateChannelTypePipe } from './pipes/guaranteed-rebate-channel-type/guaranteed-rebate-channel-type.pipe';
import { ClaimStatusPipe } from './pipes/claim-status/claim-status.pipe';
import { HrefExternalPipe } from './pipes/href-external/href-external.pipe';
import { MongoChartComponent } from './components/mongo-chart/mongo-chart.component';
import { DateRangeComponent } from './components/date-range/date-range.component';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';

@NgModule({
  declarations: [
    ConfirmDialogComponent,
    PhoneNumberPipe,
    RolesDropDownComponent,
    DropdownFilterComponent,
    MultiselectFilterComponent,
    DateFormatPipe,
    InvoiceCyclePipe,
    LobPipe,
    StatusPipe,
    StatusCellComponent,
    CbxSelectUnselectComponent,
    BooleanStatusCellComponent,
    UserPipe,
    CellGlobalStatusComponent,
    AmendmentActionPipe,
    BenefitDesignApprovalStatusPipe,
    BenefitDesignLobPipe,
    FileItemComponent,
    FileSizePipe,
    FileNamePipe,
    PreloaderComponent,
    LinkOrTextComponent,
    ContractLobPipe,
    ContractStatusPipe,
    InvoiceStatusPipe,
    HealthPlanStatusPipe,
    BlockchainStatusComponent,
    GenericAmendmentsTableComponent,
    ClaimCategoryPipe,
    ClaimFileStatusLatestPipe,
    ClaimFileStatusLogPipe,
    GridCellClaimFileStatusComponent,
    BaseLandingComponent,
    DatepickerClearDirective,
    ShortNumberPipe,
    TooltipOnEllipsisDirective,
    BooleanYesNoPipe,
    ComboboxValueToTextPipe,
    ArraySeparatorPipe,
    SplitGridCellComponent,
    PieChartCellComponent,
    ConfigurableGridComponent,
    GuaranteedRebateChannelTypePipe,
    ClaimStatusPipe,
    HrefExternalPipe,
    MongoChartComponent,
    DateRangeComponent
  ],
  imports: [
    CommonModule,
    DialogsModule,
    ButtonsModule,
    DropDownsModule,
    ReactiveFormsModule,
    IconsModule,
    InputsModule,
    DateInputsModule,
    DropDownButtonModule,
    LabelModule,
    TooltipModule,
    NgxsModule.forFeature([StateRolesDropdownService, StateBlockchainService]),
    IndicatorsModule,
    RouterModule.forChild([]),
    GridModule,
    ChartsModule
  ],
  exports: [
    DialogsModule,
    ConfirmDialogComponent,
    PhoneNumberPipe,
    RolesDropDownComponent,
    DropDownsModule,
    ReactiveFormsModule,
    DropdownFilterComponent,
    MultiselectFilterComponent,
    DateFormatPipe,
    InvoiceCyclePipe,
    LobPipe,
    StatusPipe,
    StatusCellComponent,
    CbxSelectUnselectComponent,
    BooleanStatusCellComponent,
    UserPipe,
    RoleModule,
    CellGlobalStatusComponent,
    AmendmentActionPipe,
    BenefitDesignApprovalStatusPipe,
    BenefitDesignLobPipe,
    FileItemComponent,
    FileSizePipe,
    FileNamePipe,
    PreloaderComponent,
    LinkOrTextComponent,
    ContractLobPipe,
    ContractStatusPipe,
    InvoiceStatusPipe,
    HealthPlanStatusPipe,
    BlockchainStatusComponent,
    GenericAmendmentsTableComponent,
    ClaimCategoryPipe,
    ClaimFileStatusLatestPipe,
    ClaimFileStatusLogPipe,
    GridCellClaimFileStatusComponent,
    DatepickerClearDirective,
    ShortNumberPipe,
    TooltipOnEllipsisDirective,
    BooleanYesNoPipe,
    ComboboxValueToTextPipe,
    ArraySeparatorPipe,
    SplitGridCellComponent,
    PieChartCellComponent,
    ConfigurableGridComponent,
    GuaranteedRebateChannelTypePipe,
    ClaimStatusPipe,
    HrefExternalPipe,
    MongoChartComponent,
    DateRangeComponent
  ],
  providers: [BlockchainHttpService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ModuleShared {}
